import React, { useState, useEffect, useContext } from "react";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisLabels } from "../_label/lis_labels/lis_labels.js";
import Select from "react-select";

const todayIso = () => new Date().toISOString().split("T")[0];

const EMPTY_FORM = {
  Denominazione: "",
  moGroup: "",
  moCode: "",
  ATC_Code: "",
  AntibioticClass: "",
  RSI_code: "",
  gene_code: "",
  Mechanism: "",
  Category: "",
  TemporalCriteria: "",
  // MIC: "",  // campo rimosso
  Notes: "",
  ValidFrom: todayIso(),
  WARN_HAI_ENABLED: false,
  SCHEDA_HAI_ENABLED: false,
  WARN_MOALERT_ENABLED: false,
  SCHEDA_MOALERT_ENABLED: false,
  Source: "LOCAL",
};

const toBool = (v) => v === 1 || v === true || v === "1" || v === "true";

const LisAlertRulesScreen = () => {
  const { currentUser } = useContext(UserContext);
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  // ── data ──────────────────────────────────────────────────────────────────
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ── dropdowns ─────────────────────────────────────────────────────────────
  const [moGroupOptions, setMoGroupOptions] = useState([]);
  const [moCodeOptions, setMoCodeOptions] = useState([]);
  const [atcOptions, setAtcOptions] = useState([]);
  const [antibioticClassOptions, setAntibioticClassOptions] = useState([]);
  const [rsiOptions, setRsiOptions] = useState([]);
  const [geneOptions, setGeneOptions] = useState([]);

  // ── side panel ────────────────────────────────────────────────────────────
  const [showPanel, setShowPanel] = useState(false);
  const [editingRule, setEditingRule] = useState(null); // null = new / clone
  const [cloningFrom, setCloningFrom] = useState(null); // Id sorgente per clone
  const [cloningFromLabel, setCloningFromLabel] = useState(""); // Denominazione sorgente per clone
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState(false);

  // ── search ────────────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");

  // ── mount ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    breadcrumb.set([
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      { link: "/admin/lis_alert_rules", name: "Regole Alert LIS" },
    ]);
    menu.write({ user: currentUser() });
    fetchRules();
    fetchDropdownOptions();
  }, []);

  // ── fetch rules ───────────────────────────────────────────────────────────
  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await api.sendGetRequest("admin/lis/vocabulary/alert_rules", {
        user: currentUser(),
      });
      if (res.success) setRules(res.data || []);
    } catch (e) {
      console.error("Error fetching alert rules:", e);
    } finally {
      setLoading(false);
    }
  };

  // ── fetch dropdown options ────────────────────────────────────────────────
  const fetchDropdownOptions = async () => {
    try {
      const [moRes, atcRes, rsiRes, geneRes] = await Promise.all([
        api.sendGetRequest("admin/lis/vocabulary/microorganisms", {
          user: currentUser(),
          silentMode: true,
        }),
        api.sendGetRequest("admin/lis/vocabulary/antibiotics", {
          user: currentUser(),
          silentMode: true,
        }),
        api.sendGetRequest("admin/lis/vocabulary/rsi", {
          user: currentUser(),
          silentMode: true,
        }),
        api.sendGetRequest("admin/lis/vocabulary/resistance_gene", {
          user: currentUser(),
          silentMode: true,
        }),
      ]);

      if (moRes.success) {
        const data = moRes.data || [];
        const groups = [...new Set(data.map((r) => r.moGroup).filter(Boolean))].sort();
        setMoGroupOptions(groups.map((g) => ({ value: g, label: g })));
        setMoCodeOptions(
          data.map((r) => ({
            value: r.moCode,
            label: `${r.moCode} – ${r.moMicroorganism || ""}`,
          }))
        );
      }

      if (atcRes.success) {
        const data = atcRes.data || [];
        setAtcOptions(
          data.map((r) => ({
            value: r.ATC_Code,
            label: `${r.ATC_Code} – ${r.Antibiotic || ""}`,
          }))
        );
        const classes = [...new Set(data.map((r) => r.Class).filter(Boolean))].sort();
        setAntibioticClassOptions(classes.map((c) => ({ value: c, label: c })));
      }

      if (rsiRes.success) {
        setRsiOptions(
          (rsiRes.data || []).map((r) => ({
            value: r.RSI_code,
            label: `${r.RSI_code} – ${r.RSI_description || ""}`,
          }))
        );
      }

      if (geneRes.success) {
        setGeneOptions(
          (geneRes.data || []).map((r) => ({
            value: r.gene_code,
            label: `${r.gene_code} – ${r.gene_description || ""}`,
          }))
        );
      }
    } catch (e) {
      console.error("Error fetching dropdown options:", e);
    }
  };

  // ── panel open/close ──────────────────────────────────────────────────────
  const handleAddRule = () => {
    setEditingRule(null);
    setCloningFrom(null);
    setFormData(EMPTY_FORM);
    setShowPanel(true);
  };

  const ruleToFormData = (rule) => ({
    Denominazione: rule.Denominazione || "",
    moGroup: rule.moGroup || "",
    moCode: rule.moCode || "",
    ATC_Code: rule.ATC_Code || "",
    AntibioticClass: rule.AntibioticClass || "",
    RSI_code: rule.RSI_code || "",
    gene_code: rule.gene_code || "",
    Mechanism: rule.Mechanism || "",
    Category: rule.Category || "",
    TemporalCriteria: rule.TemporalCriteria || "",
    // MIC: rule.MIC || "",  // campo rimosso
    Notes: rule.Notes || "",
    ValidFrom: rule.ValidFrom
      ? new Date(rule.ValidFrom).toISOString().split("T")[0]
      : todayIso(),
    WARN_HAI_ENABLED: toBool(rule.WARN_HAI_ENABLED),
    SCHEDA_HAI_ENABLED: toBool(rule.SCHEDA_HAI_ENABLED),
    WARN_MOALERT_ENABLED: toBool(rule.WARN_MOALERT_ENABLED),
    SCHEDA_MOALERT_ENABLED: toBool(rule.SCHEDA_MOALERT_ENABLED),
    Source: rule.Source || "LOCAL",
  });

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setCloningFrom(null);
    setFormData(ruleToFormData(rule));
    setShowPanel(true);
  };

  const handleCloneRule = (rule) => {
    setEditingRule(null); // nessun Id → crea nuovo
    setCloningFrom(rule.Id);
    setCloningFromLabel(rule.Denominazione || `#${rule.Id}`);
    setFormData(ruleToFormData(rule));
    setShowPanel(true);
  };

  const handleClosePanel = () => {
    if (formSaving) return;
    setShowPanel(false);
    setEditingRule(null);
    setCloningFrom(null);
    setCloningFromLabel("");
  };

  // ── save form ─────────────────────────────────────────────────────────────
  const handleSaveForm = async () => {
    if (!formData.moGroup && !formData.moCode) {
      MySwal.fire({
        icon: "warning",
        title: "Campi obbligatori mancanti",
        html: "Selezionare un <strong>Gruppo Microrganismo</strong> o un <strong>Microrganismo specifico</strong>.",
      });
      return;
    }
    if (!formData.ATC_Code && !formData.AntibioticClass) {
      MySwal.fire({
        icon: "warning",
        title: "Campi obbligatori mancanti",
        html: "Selezionare un <strong>Antibiotico (Codice ATC)</strong> o una <strong>Classe di antibiotici</strong>.",
      });
      return;
    }

    setFormSaving(true);
    try {
      const record = {
        ...(editingRule ? { Id: editingRule.Id } : {}),
        Denominazione: formData.Denominazione || null,
        moGroup: formData.moGroup || null,
        moCode: formData.moCode || null,
        ATC_Code: formData.ATC_Code || null,
        AntibioticClass: formData.AntibioticClass || null,
        RSI_code: formData.RSI_code || null,
        gene_code: formData.gene_code || null,
        Mechanism: formData.Mechanism,
        Category: formData.Category,
        TemporalCriteria: formData.TemporalCriteria || null,
        // MIC: formData.MIC,  // campo rimosso
        Notes: formData.Notes,
        ValidFrom: formData.ValidFrom || todayIso(),
        WARN_HAI_ENABLED: formData.WARN_HAI_ENABLED ? 1 : 0,
        SCHEDA_HAI_ENABLED: formData.SCHEDA_HAI_ENABLED ? 1 : 0,
        WARN_MOALERT_ENABLED: formData.WARN_MOALERT_ENABLED ? 1 : 0,
        SCHEDA_MOALERT_ENABLED: formData.SCHEDA_MOALERT_ENABLED ? 1 : 0,
        Source: formData.Source,
      };

      const res = await api.sendMessage("admin/lis/vocabulary/alert_rules/bulk", {
        user: currentUser(),
        body: { records: [record] },
      });

      if (res.success) {
        MySwal.fire({
          icon: "success",
          title: "Salvato",
          text: editingRule
            ? "Regola aggiornata (nuova versione creata)"
            : cloningFrom
              ? "Regola clonata con successo"
              : "Regola creata con successo",
          timer: 2000,
          showConfirmButton: false,
        });
        handleClosePanel();
        await fetchRules();
      } else {
        const detail = res.results?.errors?.[0]?.error || "";
        MySwal.fire({
          icon: "error",
          title: "Errore",
          html: detail
            ? `${res.message || "Salvataggio fallito"}<br><small class="text-danger mt-1 d-block">${detail}</small>`
            : res.message || "Salvataggio fallito",
        });
      }
    } catch (e) {
      console.error("Save error:", e);
      MySwal.fire({ icon: "error", title: "Errore di rete" });
    } finally {
      setFormSaving(false);
    }
  };

  // ── deactivate ────────────────────────────────────────────────────────────
  const handleDeactivate = async (rule) => {
    const label = rule.moGroup || rule.moCode || "";
    const abLabel = rule.ATC_Code || rule.AntibioticClass || "";
    const result = await MySwal.fire({
      icon: "warning",
      title: "Disattiva Regola",
      html: `Disattivare la regola <strong>${label} / ${abLabel}</strong>?<br><small class="text-muted">L'operazione imposta ValidTo alla data odierna (SCD Type 2)</small>`,
      showCancelButton: true,
      confirmButtonText: "Sì, disattiva",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await api.sendMessage(
        `admin/lis/vocabulary/alert_rules/${rule.Id}/delete`,
        { user: currentUser() }
      );
      if (res.success) {
        MySwal.fire({
          icon: "success",
          title: "Disattivata",
          text: "Regola disattivata correttamente",
          timer: 2000,
          showConfirmButton: false,
        });
        await fetchRules();
      } else {
        MySwal.fire({ icon: "error", title: "Errore", text: res.message || "Errore" });
      }
    } catch (e) {
      console.error("Deactivate error:", e);
    }
  };

  // ── export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setLoading(true);
    try {
      const blob = await api.downloadFileGet(
        "admin/lis/vocabulary/alert_rules/export",
        { user: currentUser() }
      );
      if (blob instanceof Blob && blob.type !== "application/json") {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `LIS_ALERT_RULES_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Export error:", e);
    } finally {
      setLoading(false);
    }
  };

  // ── import ────────────────────────────────────────────────────────────────
  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setLoading(true);
      setUploadProgress(0);
      try {
        const startRes = await api.sendMessage("admin/lis/vocabulary/upload/start", {
          user: currentUser(),
          body: { filename: file.name, totalSize: file.size },
        });
        if (!startRes.success) throw new Error(startRes.message || "Upload init failed");

        const { fileUID } = startRes;
        const chunkSize = 1024 * 1024;
        let offset = 0;
        while (offset < file.size) {
          const chunk = file.slice(offset, offset + chunkSize);
          const chunkData = Array.from(new Uint8Array(await chunk.arrayBuffer()));
          const chunkRes = await api.sendMessage("admin/lis/vocabulary/upload/chunk", {
            user: currentUser(),
            body: { fileUID, chunkData, offset },
          });
          if (!chunkRes.success) throw new Error(chunkRes.message || "Chunk failed");
          offset += chunkSize;
          setUploadProgress(Math.min(Math.round((offset / file.size) * 100), 100));
        }

        const res = await api.sendMessage("admin/lis/vocabulary/alert_rules/import", {
          user: currentUser(),
          body: { fileUID },
        });

        if (res.success) {
          const d = res.data || {};
          MySwal.fire({
            icon: "success",
            title: "Import completato",
            html: `Inseriti: <b>${d.imported ?? d.inserted ?? 0}</b><br>Aggiornati: <b>${d.updated ?? 0}</b><br>Disattivati: <b>${d.deactivated ?? 0}</b>`,
          });
          await fetchRules();
        } else {
          MySwal.fire({ icon: "error", title: "Errore import", text: res.message });
        }
      } catch (e) {
        MySwal.fire({ icon: "error", title: "Errore", text: e.message });
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    };
    input.click();
  };

  // ── helpers ───────────────────────────────────────────────────────────────
  const getAntibioticName = (atcCode) => {
    const opt = atcOptions.find((o) => o.value === atcCode);
    return opt ? opt.label.split(" – ")[1] || "" : "";
  };

  const getMoName = (moCode) => {
    const opt = moCodeOptions.find((o) => o.value === moCode);
    return opt ? opt.label.split(" – ")[1] || "" : "";
  };

  const getGeneName = (geneCode) => {
    const opt = geneOptions.find((o) => o.value === geneCode);
    return opt ? opt.label.split(" – ")[1] || "" : "";
  };

  const filteredRules = rules.filter((r) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      (r.moGroup || "").toLowerCase().includes(q) ||
      (r.moCode || "").toLowerCase().includes(q) ||
      (r.ATC_Code || "").toLowerCase().includes(q) ||
      (r.AntibioticClass || "").toLowerCase().includes(q) ||
      (r.RSI_code || "").toLowerCase().includes(q) ||
      (r.gene_code || "").toLowerCase().includes(q) ||
      (r.Mechanism || "").toLowerCase().includes(q) ||
      (r.Category || "").toLowerCase().includes(q) ||
      (r.TemporalCriteria || "").toLowerCase().includes(q) ||
      (r.Denominazione || "").toLowerCase().includes(q)
    );
  });

  const icaWarnCount   = rules.filter((r) => toBool(r.WARN_HAI_ENABLED)).length;
  const icaSchedaCount = rules.filter((r) => toBool(r.SCHEDA_HAI_ENABLED)).length;
  const alWarnCount    = rules.filter((r) => toBool(r.WARN_MOALERT_ENABLED)).length;
  const alSchedaCount  = rules.filter((r) => toBool(r.SCHEDA_MOALERT_ENABLED)).length;

  const COL_COUNT = 10;

  // ── flag indicator (read-only) ────────────────────────────────────────────
  const FlagDot = ({ active, label }) => (
    <div className="lar-flag-item">
      <span className="lar-flag-label">{label}</span>
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: active ? "#198754" : "#dee2e6",
          border: `1px solid ${active ? "#157347" : "#ced4da"}`,
        }}
        title={`${label}: ${active ? "ON" : "OFF"}`}
      />
    </div>
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .lar-toggle {
          position: relative; display: inline-block; width: 44px; height: 24px; vertical-align: middle;
        }
        .lar-toggle input { display: none; }
        .lar-slider {
          position: absolute; cursor: pointer; inset: 0;
          background: #ccc; border-radius: 24px; transition: .2s;
        }
        .lar-slider:before {
          content: ""; position: absolute;
          width: 18px; height: 18px; left: 3px; bottom: 3px;
          background: white; border-radius: 50%; transition: .2s;
        }
        input:checked + .lar-slider { background: #198754; }
        input:checked + .lar-slider:before { transform: translateX(20px); }
        input:disabled + .lar-slider { opacity: .5; cursor: not-allowed; }
        .lar-xor-sep { color: #adb5bd; font-size: 0.7rem; font-weight: 600; letter-spacing: .05em; }
        .lar-flag-row { display: flex; gap: 12px; align-items: center; justify-content: center; }
        .lar-flag-item { display: flex; flex-direction: column; align-items: center; gap: 3px; }
        .lar-flag-label { font-size: 0.65rem; color: #6c757d; white-space: nowrap; }
      `}</style>

      <div className="page-titles"><ol className="breadcrumb"></ol></div>

      <div className="container-fluid">
        <div className="card-body p-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto">

              {/* ── Header ─────────────────────────────────────────────── */}
              <div className="row mb-3">
                <div className="col-12 d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h3 className="mb-0">
                      <i className="fa-solid fa-shield-virus me-2"></i>
                      Regole Alert LIS
                    </h3>
                    <p className="text-muted mb-0 small">
                      Ogni modifica crea una nuova versione — usa i toggle per abilitare/disabilitare senza aprire il form
                    </p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    <button
                      className="btn btn-outline-info btn-sm"
                      onClick={handleExport}
                      disabled={loading}
                      title="Esporta in Excel"
                    >
                      <i className="fa-solid fa-file-arrow-down me-1"></i>
                      Esporta Excel
                    </button>
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={handleImport}
                      disabled={loading}
                      title="Importa da Excel (sostituisce tutto)"
                    >
                      <i className="fa-solid fa-file-arrow-up me-1"></i>
                      Importa Excel
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleAddRule}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-plus me-1"></i>
                      Nuova Regola
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Counters + search ──────────────────────────────────── */}
              <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 340 }}
                  placeholder="Cerca gruppo, organismo, ATC, classe, gene, RSI..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <div className="d-flex gap-2 flex-wrap">
                  <span className="badge bg-light text-dark border">
                    {filteredRules.length} regole attive
                  </span>
                  <span className="badge bg-info-subtle text-info border border-info-subtle" title="Flusso ICA — Warn abilitati">
                    <i className="fa-solid fa-triangle-exclamation me-1"></i>ICA-W {icaWarnCount}
                  </span>
                  <span className="badge bg-success-subtle text-success border border-success-subtle" title="Flusso ICA — Schede abilitate">
                    <i className="fa-solid fa-file-medical me-1"></i>ICA-S {icaSchedaCount}
                  </span>
                  <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" title="Flusso Alert — Warn abilitati">
                    <i className="fa-solid fa-triangle-exclamation me-1"></i>ALT-W {alWarnCount}
                  </span>
                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle" title="Flusso Alert — Schede abilitate">
                    <i className="fa-solid fa-bug me-1"></i>ALT-S {alSchedaCount}
                  </span>
                </div>
              </div>

              {/* ── Upload progress ────────────────────────────────────── */}
              {uploadProgress > 0 && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Caricamento file...</small>
                    <small className="text-muted">{uploadProgress}%</small>
                  </div>
                  <div className="progress" style={{ height: 6 }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* ── Table ──────────────────────────────────────────────── */}
              <div style={{ overflowX: "auto" }}>
                <table className="table table-sm table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Denominazione</th>
                      <th>Gruppo / Organismo</th>
                      <th>Antibiotico / Classe</th>
                      <th>Gene R.</th>
                      <th>RSI</th>
                      {/* <th>Meccanismo</th> */}
                      {/* <th>Criterio Temp.</th> */}
                      {/* <th>MIC</th> */}
                      <th className="text-center" title="Flusso ICA: Warn + Scheda">
                        <i className="fa-solid fa-hospital me-1 text-info"></i>ICA
                      </th>
                      <th className="text-center" title="Flusso Alert: Warn + Scheda">
                        <i className="fa-solid fa-bug me-1 text-warning"></i>Alert
                      </th>
                      <th>Sorgente</th>
                      <th>Valido Dal</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && rules.length === 0 ? (
                      <tr>
                        <td colSpan={COL_COUNT} className="text-center py-5">
                          <div className="spinner-border spinner-border-sm text-primary me-2" />
                          Caricamento...
                        </td>
                      </tr>
                    ) : filteredRules.length === 0 ? (
                      <tr>
                        <td colSpan={COL_COUNT} className="text-center text-muted py-5">
                          <i className="fa-solid fa-circle-info me-2"></i>
                          Nessuna regola trovata
                        </td>
                      </tr>
                    ) : (
                      filteredRules.map((rule) => {
                        const antibioticName = getAntibioticName(rule.ATC_Code);
                        const moName = getMoName(rule.moCode);
                        const geneName = getGeneName(rule.gene_code);

                        return (
                          <tr key={rule.Id}>
                            {/* Denominazione */}
                            <td>
                              {rule.Denominazione ? (
                                <span className="fw-semibold small">{rule.Denominazione}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            {/* Gruppo / Organismo specifico */}
                            <td>
                              {rule.moGroup ? (
                                <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle fw-normal">
                                  {rule.moGroup}
                                </span>
                              ) : rule.moCode ? (
                                <>
                                  <span className="badge bg-info-subtle text-info border border-info-subtle fw-normal">
                                    {rule.moCode}
                                  </span>
                                  {moName && (
                                    <>
                                      <br />
                                      <small className="text-muted">{moName}</small>
                                    </>
                                  )}
                                </>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Antibiotico / Classe */}
                            <td>
                              {rule.ATC_Code ? (
                                <>
                                  <span className="fw-semibold">{rule.ATC_Code}</span>
                                  {antibioticName && (
                                    <>
                                      <br />
                                      <small className="text-muted">{antibioticName}</small>
                                    </>
                                  )}
                                </>
                              ) : rule.AntibioticClass ? (
                                <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle fw-normal">
                                  {rule.AntibioticClass}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Gene di Resistenza */}
                            <td>
                              {rule.gene_code ? (
                                <>
                                  <span className="badge bg-purple-subtle text-purple border fw-normal" style={{ background: "#f3e8ff", color: "#7c3aed", borderColor: "#c4b5fd" }}>
                                    {rule.gene_code}
                                  </span>
                                  {geneName && (
                                    <>
                                      <br />
                                      <small className="text-muted">{geneName}</small>
                                    </>
                                  )}
                                </>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* RSI */}
                            <td>
                              {rule.RSI_code ? (
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle fw-normal">
                                  {rule.RSI_code}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* <td><small>{rule.Mechanism || <span className="text-muted">—</span>}</small></td> */}

                            {/* Criterio Temporale */}
                            {/* <td>
                              {rule.TemporalCriteria ? (
                                <small
                                  className="text-muted"
                                  title={rule.TemporalCriteria}
                                  style={{ maxWidth: 120, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                  <i className="fa-regular fa-clock me-1"></i>
                                  {rule.TemporalCriteria}
                                </small>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td> */}

                            {/* <td><code className="small">{rule.MIC || "—"}</code></td> */}

                            {/* Flusso ICA flags (read-only) */}
                            <td className="text-center">
                              <div className="lar-flag-row">
                                <FlagDot active={toBool(rule.WARN_HAI_ENABLED)} label="Warn" />
                                <FlagDot active={toBool(rule.SCHEDA_HAI_ENABLED)} label="Scheda" />
                              </div>
                            </td>

                            {/* Flusso Alert flags (read-only) */}
                            <td className="text-center">
                              <div className="lar-flag-row">
                                <FlagDot active={toBool(rule.WARN_MOALERT_ENABLED)} label="Warn" />
                                <FlagDot active={toBool(rule.SCHEDA_MOALERT_ENABLED)} label="Scheda" />
                              </div>
                            </td>

                            <td>
                              <span className="badge bg-light text-dark border fw-normal">
                                {rule.Source || "LOCAL"}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {rule.ValidFrom
                                  ? new Date(rule.ValidFrom).toLocaleDateString("it-IT")
                                  : "—"}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditRule(rule)}
                                  title="Modifica regola"
                                >
                                  <i className="fa-solid fa-pen-to-square"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleCloneRule(rule)}
                                  title="Clona regola"
                                >
                                  <i className="fa-solid fa-clone"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeactivate(rule)}
                                  title="Disattiva regola (SCD Type 2)"
                                >
                                  <i className="fa-solid fa-ban"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Side Panel ───────────────────────────────────────────────────── */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.35)",
              zIndex: 1040,
            }}
            onClick={handleClosePanel}
          />

          {/* Panel */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: 580,
              maxWidth: "95vw",
              backgroundColor: "white",
              boxShadow: "-3px 0 16px rgba(0,0,0,0.12)",
              zIndex: 1050,
              overflowY: "auto",
              animation: "slideInRight 0.25s ease-out",
            }}
          >
            {/* Panel header */}
            <div
              className="bg-primary text-white p-3 d-flex justify-content-between align-items-center sticky-top"
              style={{ top: 0 }}
            >
              <h5 className="mb-0">
                <i
                  className={`fa-solid ${editingRule ? "fa-pen-to-square" : cloningFrom ? "fa-clone" : "fa-plus"} me-2`}
                ></i>
                {editingRule
                  ? "Modifica Regola"
                  : cloningFrom
                    ? `Clona: "${cloningFromLabel}"`
                    : "Nuova Regola Alert"}
              </h5>
              <button
                className="btn btn-sm btn-light"
                onClick={handleClosePanel}
                disabled={formSaving}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {/* Form body */}
            <div className="p-4">
              {editingRule && (
                <div className="alert alert-info py-2 mb-3 small">
                  <i className="fa-solid fa-clock-rotate-left me-1"></i>
                  La modifica creerà una <strong>nuova versione</strong> della regola
                  (la versione precedente verrà chiusa)
                </div>
              )}
              {cloningFrom && (
                <div className="alert alert-warning py-2 mb-3 small">
                  <i className="fa-solid fa-clone me-1"></i>
                  Stai creando una <strong>copia</strong> di <em>"{cloningFromLabel}"</em> —
                  verrà salvata come nuova regola indipendente
                </div>
              )}

              {/* Denominazione */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Denominazione</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.Denominazione}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, Denominazione: e.target.value }))
                  }
                  placeholder="es. MRSA resistente a Beta-lattamine..."
                />
              </div>

              {/* ValidFrom */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="fa-regular fa-calendar me-1 text-muted"></i>
                  Valido dal <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.ValidFrom}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, ValidFrom: e.target.value }))
                  }
                />
              </div>

              {/* ── Sezione Microrganismo ─────────────────────────────── */}
              <div className="mb-3 border rounded p-3">
                <div className="fw-semibold mb-2">
                  Microrganismo <span className="text-danger">*</span>
                  <small className="text-muted fw-normal ms-2 small">seleziona gruppo o organismo specifico</small>
                </div>

                <label className="form-label small mb-1 text-muted">Microrganismo specifico</label>
                <Select
                  options={moCodeOptions}
                  value={moCodeOptions.find((o) => o.value === formData.moCode) || null}
                  onChange={(opt) =>
                    setFormData((p) => ({ ...p, moCode: opt?.value || "", moGroup: "" }))
                  }
                  placeholder="Cerca microrganismo (codice – nome)..."
                  isClearable
                  isSearchable
                  isDisabled={!!formData.moGroup}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />

                <div className="text-center my-2">
                  <span className="lar-xor-sep">— oppure —</span>
                </div>

                <label className="form-label small mb-1 text-muted">Gruppo</label>
                <Select
                  options={moGroupOptions}
                  value={moGroupOptions.find((o) => o.value === formData.moGroup) || null}
                  onChange={(opt) =>
                    setFormData((p) => ({ ...p, moGroup: opt?.value || "", moCode: "" }))
                  }
                  placeholder="Seleziona gruppo..."
                  isClearable
                  isDisabled={!!formData.moCode}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />
              </div>

              {/* ── Sezione Antibiotico ───────────────────────────────── */}
              <div className="mb-3 border rounded p-3">
                <div className="fw-semibold mb-2">
                  Antibiotico <span className="text-danger">*</span>
                  <small className="text-muted fw-normal ms-2 small">seleziona antibiotico specifico o classe</small>
                </div>

                <label className="form-label small mb-1 text-muted">Antibiotico (Codice ATC)</label>
                <Select
                  options={atcOptions}
                  value={atcOptions.find((o) => o.value === formData.ATC_Code) || null}
                  onChange={(opt) =>
                    setFormData((p) => ({ ...p, ATC_Code: opt?.value || "", AntibioticClass: "" }))
                  }
                  placeholder="Cerca antibiotico (ATC – nome)..."
                  isClearable
                  isSearchable
                  isDisabled={!!formData.AntibioticClass}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />

                <div className="text-center my-2">
                  <span className="lar-xor-sep">— oppure —</span>
                </div>

                <label className="form-label small mb-1 text-muted">Classe di antibiotici</label>
                <Select
                  options={antibioticClassOptions}
                  value={antibioticClassOptions.find((o) => o.value === formData.AntibioticClass) || null}
                  onChange={(opt) =>
                    setFormData((p) => ({ ...p, AntibioticClass: opt?.value || "", ATC_Code: "" }))
                  }
                  placeholder="Seleziona classe..."
                  isClearable
                  isDisabled={!!formData.ATC_Code}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />
              </div>

              {/* Gene di Resistenza */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Gene di Resistenza</label>
                <Select
                  options={geneOptions}
                  value={geneOptions.find((o) => o.value === formData.gene_code) || null}
                  onChange={(opt) =>
                    setFormData((p) => ({ ...p, gene_code: opt?.value || "" }))
                  }
                  placeholder="Cerca gene (codice – descrizione)..."
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />
              </div>

              {/* RSI */}
              <div className="mb-3">
                <label className="form-label fw-semibold">RSI</label>
                <Select
                  options={rsiOptions}
                  value={rsiOptions.find((o) => o.value === formData.RSI_code) || null}
                  onChange={(opt) =>
                    setFormData((p) => ({ ...p, RSI_code: opt?.value || "" }))
                  }
                  placeholder="Seleziona RSI..."
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />
              </div>

              {/* Mechanism — commentato per richiesta
              <div className="mb-3">
                <label className="form-label fw-semibold">Meccanismo di Resistenza</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.Mechanism}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, Mechanism: e.target.value }))
                  }
                  placeholder="es. ESBL, KPC, NDM..."
                />
              </div>
              */}

              {/* Category */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Categoria</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.Category}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, Category: e.target.value }))
                  }
                />
              </div>

              {/* Criterio Temporale */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="fa-regular fa-clock me-1 text-muted"></i>
                  Criterio Temporale
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.TemporalCriteria}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, TemporalCriteria: e.target.value }))
                  }
                  placeholder="es. data_accettazione - data_ricovero >= 48h"
                />
                <div className="form-text text-muted small">
                  Espressione che definisce la finestra temporale che attiva la regola (es. insorgenza ≥ 48h dal ricovero per sospetta ICA)
                </div>
              </div>

              {/* MIC — campo rimosso, commentato per eventuale ripristino
              <div className="mb-3">
                <label className="form-label fw-semibold">MIC (Soglia)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.MIC}
                  onChange={(e) => setFormData((p) => ({ ...p, MIC: e.target.value }))}
                  placeholder="es. ≥2"
                  style={{ fontFamily: "monospace" }}
                />
              </div>
              */}

              {/* Notes */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Note</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.Notes}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, Notes: e.target.value }))
                  }
                />
              </div>

              {/* ── Flusso ICA ───────────────────────────────────────── */}
              <div className="mb-3 border rounded p-3">
                <div className="fw-semibold mb-2">
                  <i className="fa-solid fa-hospital me-1 text-info"></i>
                  Flusso ICA (Infezione Correlata all'Assistenza)
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="d-flex align-items-start gap-2 p-2 rounded bg-light">
                      <label className="lar-toggle mt-1" style={{ flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.WARN_HAI_ENABLED}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, WARN_HAI_ENABLED: e.target.checked }))
                          }
                        />
                        <span className="lar-slider"></span>
                      </label>
                      <div>
                        <div className="fw-semibold small">Warn ICA</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          Avviso email ai ruoli ICA — non crea scheda
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-start gap-2 p-2 rounded bg-light">
                      <label className="lar-toggle mt-1" style={{ flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.SCHEDA_HAI_ENABLED}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, SCHEDA_HAI_ENABLED: e.target.checked }))
                          }
                        />
                        <span className="lar-slider"></span>
                      </label>
                      <div>
                        <div className="fw-semibold small">Scheda ICA</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          Propone la scheda ICA configurata ai ruoli abilitati
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Flusso Alert ─────────────────────────────────────── */}
              <div className="mb-4 border rounded p-3">
                <div className="fw-semibold mb-2">
                  <i className="fa-solid fa-bug me-1 text-warning"></i>
                  Flusso Alert
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="d-flex align-items-start gap-2 p-2 rounded bg-light">
                      <label className="lar-toggle mt-1" style={{ flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.WARN_MOALERT_ENABLED}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, WARN_MOALERT_ENABLED: e.target.checked }))
                          }
                        />
                        <span className="lar-slider"></span>
                      </label>
                      <div>
                        <div className="fw-semibold small">Warn Alert</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          Avviso email ai gruppi Alert — non crea scheda
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-start gap-2 p-2 rounded bg-light">
                      <label className="lar-toggle mt-1" style={{ flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.SCHEDA_MOALERT_ENABLED}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, SCHEDA_MOALERT_ENABLED: e.target.checked }))
                          }
                        />
                        <span className="lar-slider"></span>
                      </label>
                      <div>
                        <div className="fw-semibold small">Scheda Alert</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          Propone la scheda Alert configurata ai gruppi abilitati
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Source */}
              <div className="mb-4">
                <label className="form-label fw-semibold">Sorgente</label>
                <select
                  className="form-select"
                  value={formData.Source}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, Source: e.target.value }))
                  }
                >
                  <option value="LOCAL">LOCAL</option>
                  <option value="NOMOS">NOMOS</option>
                  <option value="WHO">WHO</option>
                </select>
              </div>

              {/* Footer */}
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-secondary"
                  onClick={handleClosePanel}
                  disabled={formSaving}
                >
                  Annulla
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveForm}
                  disabled={formSaving}
                >
                  {formSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <i className={`fa-solid ${editingRule ? "fa-check" : cloningFrom ? "fa-clone" : "fa-plus"} me-1`}></i>
                      {editingRule ? "Aggiorna Regola" : cloningFrom ? "Salva Clone" : "Crea Regola"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LisAlertRulesScreen;
